import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Mail, 
  Send, 
  FileText, 
  BarChart3,
  Plus,
  Search,
  Filter,
  Edit,
  Trash2,
  Eye,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Clock,
  RotateCcw
} from 'lucide-react';

// Components
import AdminHeader from '../../../shared/components/AdminHeader';

// Services
import { adminEmailAPI } from '../../../shared/services/emailAPI';
import { apiService } from '../../../shared/services/api';

// Types
interface EmailStats {
  overview: {
    total: number;
    sent: number;
    failed: number;
    pending: number;
    bounced: number;
    opened: number;
    clicked: number;
  };
  recent24h: {
    total: number;
    sent: number;
    failed: number;
    pending: number;
    bounced: number;
    opened: number;
    clicked: number;
  };
  templateStats: Array<{
    templateType: string;
    count: number;
    sent?: number;
    failed?: number;
    openRate?: number;
    clickRate?: number;
  }>;
  deliveryStats: Array<{
    _id: string;
    total: number;
    sent: number;
    failed: number;
  }>;
}

interface EmailLog {
  _id: string;
  toEmail: string;
  subject: string;
  status: 'pending' | 'sent' | 'failed' | 'bounced' | 'opened' | 'clicked';
  templateType?: string;
  campaignName?: string;
  createdAt: string;
  sentAt?: string;
  failureReason?: string;
  userId?: {
    _id: string;
    email: string;
    name?: string;
  };
  senderAdminId?: {
    _id: string;
    email: string;
    name?: string;
  };
}

interface EmailTemplate {
  _id: string;
  name: string;
  description?: string;
  templateType: string;
  category?: string;
  subject: string;
  htmlContent: string;
  textContent: string;
  variables: Array<{
    name: string;
    type: string;
    description?: string;
    defaultValue?: string;
  }>;
  isActive: boolean;
  isSystem: boolean;
  usageCount: number;
  createdBy?: {
    _id: string;
    email: string;
    name?: string;
  };
  createdAt: string;
  updatedAt: string;
}

type ActiveTab = 'overview' | 'logs' | 'templates' | 'send';

export default function EmailManagementPage() {
  const [activeTab, setActiveTab] = useState<ActiveTab>('overview');
  const [filters, setFilters] = useState<any>({}); // 필터 기능 구현 시 사용
  const queryClient = useQueryClient();

  // 이메일 통계 데이터
  const { data: emailStats, isLoading: statsLoading } = useQuery({
    queryKey: ['emailStats', 30],
    queryFn: () => adminEmailAPI.getStats(30)
  });

  // 이메일 로그 데이터
  const { data: emailLogsData, isLoading: logsLoading } = useQuery({
    queryKey: ['emailLogs', filters],
    queryFn: () => adminEmailAPI.getLogs(filters)
  });

  // 이메일 템플릿 데이터
  const { data: emailTemplatesData, isLoading: templatesLoading } = useQuery({
    queryKey: ['emailTemplates'],
    queryFn: () => adminEmailAPI.getTemplates()
  });

  // 메일 발송 mutation
  const sendEmailMutation = useMutation({
    mutationFn: adminEmailAPI.sendEmail,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emailLogs'] });
      queryClient.invalidateQueries({ queryKey: ['emailStats'] });
    }
  });

  // 템플릿 삭제 mutation
  const deleteTemplateMutation = useMutation({
    mutationFn: adminEmailAPI.deleteTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emailTemplates'] });
    }
  });

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <AdminHeader 
        title="메일 관리"
        subtitle="발송된 메일 확인 및 새로운 메일 템플릿 관리"
      />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          {/* 탭 네비게이션 */}
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="-mb-px flex space-x-8">
              {[
                { id: 'overview', name: '개요', icon: BarChart3 },
                { id: 'logs', name: '메일 로그', icon: Mail },
                { id: 'templates', name: '템플릿', icon: FileText },
                { id: 'send', name: '메일 발송', icon: Send }
              ].map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as ActiveTab)}
                    className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                        : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                    }`}
                  >
                    <Icon className="w-4 h-4 mr-2" />
                    {tab.name}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* 탭 컨텐츠 */}
          <div className="mt-6">
            {activeTab === 'overview' && (
              <EmailOverviewTab stats={emailStats} loading={statsLoading} />
            )}
            {activeTab === 'logs' && (
              <EmailLogsTab 
                logs={emailLogsData?.logs || []} 
                pagination={emailLogsData?.pagination}
                loading={logsLoading}
                filters={filters}
                setFilters={setFilters}
              />
            )}
            {activeTab === 'templates' && (
              <EmailTemplatesTab 
                templates={emailTemplatesData?.templates || []}
                pagination={emailTemplatesData?.pagination}
                loading={templatesLoading}
                onDeleteTemplate={(id) => deleteTemplateMutation.mutate(id)}
                deleteLoading={deleteTemplateMutation.isPending}
              />
            )}
            {activeTab === 'send' && (
              <EmailSendTab 
                onSendEmail={sendEmailMutation.mutate}
                sendLoading={sendEmailMutation.isPending}
                templates={emailTemplatesData?.templates || []}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// 이메일 개요 탭
function EmailOverviewTab({ stats, loading }: { stats?: EmailStats & { hasData?: boolean }; loading: boolean }) {
  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 animate-pulse">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2"></div>
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
          </div>
        ))}
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 text-center">
        <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          통계 데이터를 불러올 수 없습니다
        </h3>
        <p className="text-gray-500 dark:text-gray-400 mb-4">
          서버 연결에 문제가 있습니다. 잠시 후 다시 시도해주세요.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <RotateCcw className="w-4 h-4 mr-2" />
          새로고침
        </button>
      </div>
    );
  }

  // 데이터가 없는 경우 안내
  if (stats.hasData === false) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 text-center">
        <Mail className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          아직 발송된 메일이 없습니다
        </h3>
        <p className="text-gray-500 dark:text-gray-400 mb-4">
          메일 발송 탭에서 첫 번째 메일을 발송해보세요.
        </p>
        <button
          onClick={() => window.location.href = '#send'}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <Send className="w-4 h-4 mr-2" />
          메일 발송하기
        </button>
      </div>
    );
  }

  const overviewCards = [
    {
      title: '총 발송',
      value: (stats.overview?.total || 0).toLocaleString(),
      icon: Mail,
      color: 'blue',
      change: stats.recent24h?.total || 0
    },
    {
      title: '성공적 발송',
      value: (stats.overview?.sent || 0).toLocaleString(),
      icon: CheckCircle,
      color: 'green',
      change: stats.recent24h?.sent || 0
    },
    {
      title: '실패',
      value: (stats.overview?.failed || 0).toLocaleString(),
      icon: AlertCircle,
      color: 'red',
      change: stats.recent24h?.failed || 0
    },
    {
      title: '대기 중',
      value: (stats.overview?.pending || 0).toLocaleString(),
      icon: Clock,
      color: 'yellow',
      change: stats.recent24h?.pending || 0
    },
    {
      title: '반송',
      value: (stats.overview?.bounced || 0).toLocaleString(),
      icon: RotateCcw,
      color: 'orange',
      change: stats.recent24h?.bounced || 0
    },
    {
      title: '열람',
      value: (stats.overview?.opened || 0).toLocaleString(),
      icon: Eye,
      color: 'purple',
      change: stats.recent24h?.opened || 0
    },
    {
      title: '클릭',
      value: (stats.overview?.clicked || 0).toLocaleString(),
      icon: TrendingUp,
      color: 'indigo',
      change: stats.recent24h?.clicked || 0
    },
    {
      title: '성공률',
      value: (stats.overview?.total || 0) > 0 
        ? `${(((stats.overview?.sent || 0) / (stats.overview?.total || 1)) * 100).toFixed(1)}%`
        : '0%',
      icon: BarChart3,
      color: 'emerald',
      change: 0
    }
  ];

  const colorClasses = {
    blue: 'text-blue-600 bg-blue-50 dark:bg-blue-900/20',
    green: 'text-green-600 bg-green-50 dark:bg-green-900/20',
    red: 'text-red-600 bg-red-50 dark:bg-red-900/20',
    yellow: 'text-yellow-600 bg-yellow-50 dark:bg-yellow-900/20',
    orange: 'text-orange-600 bg-orange-50 dark:bg-orange-900/20',
    purple: 'text-purple-600 bg-purple-50 dark:bg-purple-900/20',
    indigo: 'text-indigo-600 bg-indigo-50 dark:bg-indigo-900/20',
    emerald: 'text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20'
  };

  return (
    <div className="space-y-6">
      {/* 통계 카드 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {overviewCards.map((card) => {
          const Icon = card.icon;
          const colorClass = colorClasses[card.color as keyof typeof colorClasses];
          
          return (
            <div key={card.title} className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    {card.title}
                  </p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {card.value}
                  </p>
                  {(card.change || 0) > 0 && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      24시간: +{(card.change || 0).toLocaleString()}
                    </p>
                  )}
                </div>
                <div className={`p-3 rounded-full ${colorClass}`}>
                  <Icon className="w-6 h-6" />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* 템플릿별 통계 */}
      {stats.templateStats && stats.templateStats.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            템플릿별 사용 통계
          </h3>
          <div className="space-y-3">
            {stats.templateStats.map((stat, index) => (
              <div key={index} className="flex items-center justify-between py-2">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {stat.templateType || '알 수 없음'}
                </span>
                <div className="flex items-center space-x-3">
                  <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2 w-32">
                    <div 
                      className="bg-blue-600 h-2 rounded-full" 
                      style={{ 
                        width: `${((stat.count || 0) / Math.max(...stats.templateStats.map(s => s.count || 0))) * 100}%` 
                      }}
                    ></div>
                  </div>
                  <span className="text-sm text-gray-600 dark:text-gray-400 min-w-[3rem] text-right">
                    {(stat.count || 0).toLocaleString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// 이메일 로그 탭
function EmailLogsTab({ 
  logs, 
  pagination, 
  loading, 
  filters, 
  setFilters 
}: { 
  logs: EmailLog[]; 
  pagination?: any; 
  loading: boolean;
  filters: any;
  setFilters: (filters: any) => void;
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateRange, setDateRange] = useState({ from: '', to: '' });

  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400',
    sent: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400',
    failed: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400',
    bounced: 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400',
    opened: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400',
    clicked: 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400'
  };

  const statusLabels = {
    pending: '대기',
    sent: '발송완료',
    failed: '실패',
    bounced: '반송',
    opened: '열람',
    clicked: '클릭'
  };

  const handleSearch = () => {
    setFilters({
      ...filters,
      search: searchTerm,
      status: statusFilter,
      dateFrom: dateRange.from,
      dateTo: dateRange.to
    });
  };

  const handleReset = () => {
    setSearchTerm('');
    setStatusFilter('');
    setDateRange({ from: '', to: '' });
    setFilters({});
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="p-6 animate-pulse">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 dark:bg-gray-700 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
      {/* 필터 및 검색 */}
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">메일 로그</h3>
          
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="이메일, 제목, 캠페인명 검색..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-sm"
              />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-sm"
              >
                <option value="">전체 상태</option>
                <option value="pending">대기</option>
                <option value="sent">발송완료</option>
                <option value="failed">실패</option>
                <option value="bounced">반송</option>
                <option value="opened">열람</option>
                <option value="clicked">클릭</option>
              </select>
            </div>
            
            <div className="flex gap-2">
              <input
                type="date"
                value={dateRange.from}
                onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-sm"
              />
              <input
                type="date"
                value={dateRange.to}
                onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-sm"
              />
            </div>
            
            <div className="flex gap-2">
              <button
                onClick={handleSearch}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 text-sm flex items-center gap-2"
              >
                <Search className="w-4 h-4" />
                검색
              </button>
              <button
                onClick={handleReset}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-sm flex items-center gap-2"
              >
                <Filter className="w-4 h-4" />
                초기화
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 로그 목록 */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                수신자
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                제목
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                상태
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                템플릿
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                발송일시
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                발송자
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {logs.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center">
                  <Mail className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500 dark:text-gray-400">메일 로그가 없습니다.</p>
                </td>
              </tr>
            ) : (
              logs.map((log) => (
                <tr key={log._id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {log.toEmail}
                    </div>
                    {log.userId && (
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {log.userId.name || log.userId.email}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900 dark:text-white max-w-xs truncate">
                      {log.subject}
                    </div>
                    {log.campaignName && (
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        캠페인: {log.campaignName}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      statusColors[log.status as keyof typeof statusColors]
                    }`}>
                      {statusLabels[log.status as keyof typeof statusLabels]}
                    </span>
                    {log.failureReason && (
                      <div className="text-xs text-red-600 dark:text-red-400 mt-1 max-w-xs truncate">
                        {log.failureReason}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {log.templateType || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    <div>{new Date(log.createdAt).toLocaleDateString()}</div>
                    <div>{new Date(log.createdAt).toLocaleTimeString()}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {log.senderAdminId ? 
                      (log.senderAdminId.name || log.senderAdminId.email) : 
                      '시스템'
                    }
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* 페이지네이션 */}
      {pagination && pagination.totalPages > 1 && (
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700 dark:text-gray-300">
              총 {(pagination?.totalLogs || 0).toLocaleString()}개 중 {((pagination?.page - 1 || 0) * (pagination?.limit || 10)) + 1}-
              {Math.min((pagination?.page || 1) * (pagination?.limit || 10), pagination?.totalLogs || 0)}개 표시
            </div>
            <div className="flex gap-2">
              <button
                disabled={!pagination.hasPrevPage}
                className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                이전
              </button>
              <span className="px-3 py-1 text-sm">
                {pagination.page} / {pagination.totalPages}
              </span>
              <button
                disabled={!pagination.hasNextPage}
                className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                다음
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// 이메일 템플릿 탭
function EmailTemplatesTab({ 
  templates, 
  loading, 
  onDeleteTemplate, 
  deleteLoading 
}: { 
  templates: EmailTemplate[]; 
  pagination?: any; 
  loading: boolean;
  onDeleteTemplate: (id: string) => void;
  deleteLoading: boolean;
}) {
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const queryClient = useQueryClient();

  // 템플릿 생성 mutation
  const createTemplateMutation = useMutation({
    mutationFn: adminEmailAPI.createTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emailTemplates'] });
      setShowCreateModal(false);
    }
  });

  // 템플릿 수정 mutation
  const updateTemplateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => 
      adminEmailAPI.updateTemplate(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emailTemplates'] });
      setEditingTemplate(null);
    }
  });

  // 개발중 - 템플릿 생성/수정 기능 활성화 예정
  console.log('Template modal states:', { showCreateModal, editingTemplate, createTemplateMutation, updateTemplateMutation });

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="animate-pulse space-y-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">메일 템플릿</h3>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            새 템플릿
          </button>
        </div>
      </div>

      {/* 템플릿 목록 */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {templates.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400">등록된 템플릿이 없습니다.</p>
            </div>
          ) : (
            templates.map((template) => (
              <div key={template._id} className="p-6 hover:bg-gray-50 dark:hover:bg-gray-700">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <h4 className="text-lg font-medium text-gray-900 dark:text-white">
                        {template.name}
                      </h4>
                      <div className="flex gap-2">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400">
                          {template.templateType}
                        </span>
                        {template.isSystem && (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400">
                            시스템
                          </span>
                        )}
                        {!template.isActive && (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400">
                            비활성
                          </span>
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {template.description || '설명 없음'}
                    </p>
                    <div className="flex items-center gap-4 mt-2 text-sm text-gray-500 dark:text-gray-400">
                      <span>제목: {template.subject}</span>
                      <span>사용 횟수: {(template.usageCount || 0).toLocaleString()}</span>
                      {template.createdBy && (
                        <span>생성자: {template.createdBy.name || template.createdBy.email}</span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setSelectedTemplate(template)}
                      className="p-2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                      title="상세 보기"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    {/* 시스템 템플릿도 편집 가능 */}
                    <button
                      onClick={() => setEditingTemplate(template)}
                      className="p-2 text-gray-400 hover:text-green-600 dark:hover:text-green-400"
                      title={template.isSystem ? "시스템 템플릿 편집" : "편집"}
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    {/* 시스템 템플릿은 삭제만 제한 */}
                    {!template.isSystem && (
                      <button
                        onClick={() => {
                          if (confirm('이 템플릿을 삭제하시겠습니까?')) {
                            onDeleteTemplate(template._id);
                          }
                        }}
                        disabled={deleteLoading}
                        className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 disabled:opacity-50"
                        title="삭제"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* 템플릿 상세 모달 */}
      {selectedTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  템플릿 상세: {selectedTemplate.name}
                </h3>
                <button
                  onClick={() => setSelectedTemplate(null)}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  ✕
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    템플릿명
                  </label>
                  <p className="mt-1 text-sm text-gray-900 dark:text-white">{selectedTemplate.name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    타입
                  </label>
                  <p className="mt-1 text-sm text-gray-900 dark:text-white">{selectedTemplate.templateType}</p>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  제목
                </label>
                <p className="mt-1 text-sm text-gray-900 dark:text-white">{selectedTemplate.subject}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  HTML 내용
                </label>
                <div className="mt-1 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <pre className="text-xs text-gray-900 dark:text-white whitespace-pre-wrap overflow-x-auto">
                    {selectedTemplate.htmlContent}
                  </pre>
                </div>
              </div>
              
              {selectedTemplate.variables.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    변수
                  </label>
                  <div className="mt-1 space-y-2">
                    {selectedTemplate.variables.map((variable, index) => (
                      <div key={index} className="p-2 bg-gray-50 dark:bg-gray-700 rounded">
                        <div className="text-sm">
                          <span className="font-medium">{variable.name}</span>
                          <span className="text-gray-500 ml-2">({variable.type})</span>
                        </div>
                        {variable.description && (
                          <p className="text-xs text-gray-600 dark:text-gray-400">{variable.description}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 새 템플릿 생성 모달 */}
      {showCreateModal && (
        <TemplateFormModal
          title="새 템플릿 생성"
          onSubmit={createTemplateMutation.mutate}
          onClose={() => setShowCreateModal(false)}
          isLoading={createTemplateMutation.isPending}
        />
      )}

      {/* 템플릿 수정 모달 */}
      {editingTemplate && (
        <TemplateFormModal
          title="템플릿 수정"
          template={editingTemplate}
          onSubmit={(data) => updateTemplateMutation.mutate({ id: editingTemplate._id, data })}
          onClose={() => setEditingTemplate(null)}
          isLoading={updateTemplateMutation.isPending}
        />
      )}
    </div>
  );
}

function EmailSendTab({ onSendEmail, sendLoading, templates }: any) {
  const [emailForm, setEmailForm] = useState({
    toEmail: '',
    subject: '',
    htmlContent: '',
    templateId: ''
  });
  const [recipientType, setRecipientType] = useState<'manual' | 'user'>('manual');
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [showUserSelector, setShowUserSelector] = useState(false);
  
  // 사용자 목록 데이터
  const { data: usersData, isLoading: usersLoading } = useQuery({
    queryKey: ['adminUsers'],
    queryFn: () => apiService.client.get('/admin/users').then(res => res.data.data),
    enabled: recipientType === 'user'
  });

  // 템플릿 선택 시 자동 반영
  const handleTemplateChange = (templateId: string) => {
    const selectedTemplate = templates?.find((t: any) => t._id === templateId);
    if (selectedTemplate) {
      setEmailForm(prev => ({
        ...prev,
        templateId,
        subject: selectedTemplate.subject,
        htmlContent: selectedTemplate.htmlContent
      }));
    } else {
      setEmailForm(prev => ({
        ...prev,
        templateId: '',
        subject: '',
        htmlContent: ''
      }));
    }
  };

  // 사용자 선택 토글
  const toggleUserSelection = (userId: string) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  // 모든 사용자 선택/해제
  const toggleAllUsers = () => {
    if (selectedUsers.length === usersData?.users?.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(usersData?.users?.map((user: any) => user._id) || []);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // 수신자 검증
    if (recipientType === 'manual') {
      if (!emailForm.toEmail) {
        alert('수신자 이메일을 입력해주세요.');
        return;
      }
    } else {
      if (selectedUsers.length === 0) {
        alert('발송할 사용자를 선택해주세요.');
        return;
      }
    }

    if (!emailForm.subject) {
      alert('제목을 입력해주세요.');
      return;
    }

    try {
      if (recipientType === 'manual') {
        // 개별 메일 발송
        await onSendEmail({
          toEmail: emailForm.toEmail,
          subject: emailForm.subject,
          htmlContent: emailForm.htmlContent || '<p>테스트 메일입니다.</p>',
          textContent: emailForm.htmlContent ? emailForm.htmlContent.replace(/<[^>]*>/g, '') : '테스트 메일입니다.',
          templateId: emailForm.templateId || undefined
        });
      } else {
        // 선택된 사용자들에게 벌크 메일 발송
        const selectedUserEmails = usersData?.users
          ?.filter((user: any) => selectedUsers.includes(user._id))
          ?.map((user: any) => user.email) || [];

        await adminEmailAPI.sendBulkEmail({
          recipients: selectedUserEmails,
          subject: emailForm.subject,
          htmlContent: emailForm.htmlContent || '<p>테스트 메일입니다.</p>',
          textContent: emailForm.htmlContent ? emailForm.htmlContent.replace(/<[^>]*>/g, '') : '테스트 메일입니다.',
          templateId: emailForm.templateId || undefined,
          campaignName: `선택된 사용자 발송 - ${new Date().toISOString().split('T')[0]}`
        });
      }
      
      alert('메일이 성공적으로 발송되었습니다!');
      setEmailForm({
        toEmail: '',
        subject: '',
        htmlContent: '',
        templateId: ''
      });
      setSelectedUsers([]);
    } catch (error) {
      console.error('메일 발송 오류:', error);
      alert('메일 발송에 실패했습니다.');
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">메일 발송</h3>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* 수신자 타입 선택 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            수신자 선택 방법
          </label>
          <div className="flex space-x-4">
            <label className="flex items-center">
              <input
                type="radio"
                name="recipientType"
                value="manual"
                checked={recipientType === 'manual'}
                onChange={(e) => setRecipientType(e.target.value as 'manual' | 'user')}
                className="mr-2"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">수동 입력</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="recipientType"
                value="user"
                checked={recipientType === 'user'}
                onChange={(e) => setRecipientType(e.target.value as 'manual' | 'user')}
                className="mr-2"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">기존 사용자 선택</span>
            </label>
          </div>
        </div>

        {/* 수신자 입력 */}
        {recipientType === 'manual' ? (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              수신자 이메일 *
            </label>
            <input
              type="email"
              value={emailForm.toEmail}
              onChange={(e) => setEmailForm(prev => ({ ...prev, toEmail: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder="recipient@example.com"
              required
            />
          </div>
        ) : (
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                사용자 선택 * ({selectedUsers.length}명 선택됨)
              </label>
              <button
                type="button"
                onClick={() => setShowUserSelector(!showUserSelector)}
                className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                {showUserSelector ? '닫기' : '사용자 선택'}
              </button>
            </div>
            
            {showUserSelector && (
              <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 max-h-64 overflow-y-auto bg-gray-50 dark:bg-gray-700">
                {usersLoading ? (
                  <div className="text-center py-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">사용자 목록 로딩 중...</p>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-200 dark:border-gray-600">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        총 {usersData?.users?.length || 0}명의 사용자
                      </span>
                      <button
                        type="button"
                        onClick={toggleAllUsers}
                        className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400"
                      >
                        {selectedUsers.length === usersData?.users?.length ? '전체 해제' : '전체 선택'}
                      </button>
                    </div>
                    
                    <div className="space-y-2">
                      {usersData?.users?.map((user: any) => (
                        <label key={user._id} className="flex items-center p-2 hover:bg-gray-100 dark:hover:bg-gray-600 rounded cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedUsers.includes(user._id)}
                            onChange={() => toggleUserSelection(user._id)}
                            className="mr-3"
                          />
                          <div className="flex-1">
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {user.name || user.email}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {user.email}
                              {user.name && ` • 가입일: ${new Date(user.createdAt).toLocaleDateString()}`}
                            </div>
                          </div>
                          <div className="text-xs text-gray-400">
                            {user.stats?.totalUrls || 0} 링크
                          </div>
                        </label>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* 템플릿 선택 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            템플릿 선택 (선택사항)
          </label>
          <select
            value={emailForm.templateId}
            onChange={(e) => handleTemplateChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
          >
            <option value="">직접 입력</option>
            {templates?.map((template: any) => (
              <option key={template._id} value={template._id}>
                {template.name} - {template.templateType}
              </option>
            ))}
          </select>
          {emailForm.templateId && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              템플릿 선택 시 제목과 내용이 자동으로 입력됩니다.
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            제목 *
          </label>
          <input
            type="text"
            value={emailForm.subject}
            onChange={(e) => setEmailForm(prev => ({ ...prev, subject: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            placeholder="메일 제목을 입력하세요"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            내용 (HTML)
          </label>
          <textarea
            value={emailForm.htmlContent}
            onChange={(e) => setEmailForm(prev => ({ ...prev, htmlContent: e.target.value }))}
            rows={8}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            placeholder="<p>메일 내용을 HTML로 입력하세요.</p>"
          />
        </div>

        <div className="flex gap-3">
          <button
            type="submit"
            disabled={sendLoading}
            className={`px-4 py-2 rounded-lg text-white font-medium ${
              sendLoading 
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-blue-600 hover:bg-blue-700 focus:ring-2 focus:ring-blue-500'
            }`}
          >
            {sendLoading ? '발송 중...' : (recipientType === 'user' && selectedUsers.length > 1 ? `${selectedUsers.length}명에게 발송` : '메일 발송')}
          </button>
          
          <button
            type="button"
            onClick={() => {
              setEmailForm({
                toEmail: '',
                subject: '',
                htmlContent: '',
                templateId: ''
              });
              setSelectedUsers([]);
            }}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            초기화
          </button>
        </div>
      </form>
    </div>
  );
}

// 템플릿 폼 모달 컴포넌트 - 개발중
function TemplateFormModal({ 
  title, 
  template, 
  onSubmit, 
  onClose, 
  isLoading 
}: {
  title: string;
  template?: EmailTemplate;
  onSubmit: (data: any) => void;
  onClose: () => void;
  isLoading: boolean;
}) {
  const [formData, setFormData] = useState({
    name: template?.name || '',
    description: template?.description || '',
    templateType: template?.templateType || 'promotional',
    category: template?.category || '',
    subject: template?.subject || '',
    htmlContent: template?.htmlContent || '',
    textContent: template?.textContent || '',
    isActive: template?.isActive ?? true,
    variables: template?.variables || []
  });

  const [newVariable, setNewVariable] = useState({
    name: '',
    type: 'string',
    description: '',
    defaultValue: ''
  });

  const templateTypes = [
    { value: 'promotional', label: '프로모션' },
    { value: 'transactional', label: '트랜잭션' },
    { value: 'notification', label: '알림' },
    { value: 'newsletter', label: '뉴스레터' },
    { value: 'verification', label: '인증' },
    { value: 'welcome', label: '환영' },
    { value: 'reminder', label: '리마인더' },
    { value: 'other', label: '기타' }
  ];

  const variableTypes = [
    { value: 'string', label: '문자열' },
    { value: 'number', label: '숫자' },
    { value: 'date', label: '날짜' },
    { value: 'url', label: 'URL' },
    { value: 'email', label: '이메일' }
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.subject || !formData.htmlContent) {
      alert('템플릿명, 제목, HTML 내용은 필수입니다.');
      return;
    }

    // 시스템 템플릿 수정 시 확인
    if (template?.isSystem) {
      const confirmMsg = `이 템플릿은 시스템 템플릿입니다.\n\n수정 시 다음과 같은 영향이 있을 수 있습니다:\n• 사용자 인증 이메일에 영향\n• 알림 이메일 형식 변경\n• 시스템 전체 이메일 동작 변경\n\n정말 수정하시겠습니까?`;
      
      if (!confirm(confirmMsg)) {
        return;
      }
    }

    onSubmit({
      ...formData,
      textContent: formData.textContent || formData.htmlContent.replace(/<[^>]*>/g, '')
    });
  };

  const addVariable = () => {
    if (!newVariable.name || !newVariable.type) {
      alert('변수명과 타입은 필수입니다.');
      return;
    }

    setFormData(prev => ({
      ...prev,
      variables: [...prev.variables, { ...newVariable }]
    }));

    setNewVariable({
      name: '',
      type: 'string',
      description: '',
      defaultValue: ''
    });
  };

  const removeVariable = (index: number) => {
    setFormData(prev => ({
      ...prev,
      variables: prev.variables.filter((_, i) => i !== index)
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                {title}
              </h3>
              {template?.isSystem && (
                <div className="mt-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <div className="flex items-center">
                    <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400 mr-2" />
                    <span className="text-sm font-medium text-amber-800 dark:text-amber-300">
                      시스템 템플릿 편집
                    </span>
                  </div>
                  <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">
                    이 템플릿은 시스템에서 자동으로 사용되는 템플릿입니다. 
                    수정 시 이메일 발송에 영향을 줄 수 있으니 신중히 편집해주세요.
                  </p>
                </div>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              ✕
            </button>
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* 기본 정보 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                템플릿명 *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="템플릿명을 입력하세요"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                템플릿 타입 *
              </label>
              <select
                value={formData.templateType}
                onChange={(e) => setFormData(prev => ({ ...prev, templateType: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                required
                disabled={template?.isSystem} // 시스템 템플릿은 타입 변경 불가
              >
                {templateTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
              {template?.isSystem && (
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                  시스템 템플릿의 타입은 변경할 수 없습니다.
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                카테고리
              </label>
              <input
                type="text"
                value={formData.category}
                onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="예: 마케팅, 시스템 등"
              />
            </div>
            
            <div className="flex items-center">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                  className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  disabled={template?.isSystem} // 시스템 템플릿은 비활성화 불가
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">활성 상태</span>
              </label>
              {template?.isSystem && (
                <p className="text-xs text-amber-600 dark:text-amber-400 ml-4">
                  시스템 템플릿은 항상 활성 상태여야 합니다.
                </p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              설명
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder="템플릿에 대한 설명을 입력하세요"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              제목 *
            </label>
            <input
              type="text"
              value={formData.subject}
              onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder="이메일 제목을 입력하세요"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              HTML 내용 *
            </label>
            <textarea
              value={formData.htmlContent}
              onChange={(e) => setFormData(prev => ({ ...prev, htmlContent: e.target.value }))}
              rows={12}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white font-mono text-sm"
              placeholder="HTML 내용을 입력하세요. 변수는 {{변수명}} 형태로 사용하세요."
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              텍스트 내용
            </label>
            <textarea
              value={formData.textContent}
              onChange={(e) => setFormData(prev => ({ ...prev, textContent: e.target.value }))}
              rows={6}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder="텍스트 버전 (선택사항, 비어있으면 HTML에서 자동 생성)"
            />
          </div>

          {/* 변수 관리 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              변수 관리
            </label>
            
            {/* 기존 변수 목록 */}
            {formData.variables.length > 0 && (
              <div className="mb-4 space-y-2">
                {formData.variables.map((variable, index) => (
                  <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-700 rounded">
                    <span className="font-medium text-sm">{variable.name}</span>
                    <span className="text-xs text-gray-500">({variable.type})</span>
                    {variable.description && (
                      <span className="text-xs text-gray-600 dark:text-gray-400">- {variable.description}</span>
                    )}
                    <button
                      type="button"
                      onClick={() => removeVariable(index)}
                      className="ml-auto text-red-600 hover:text-red-800 text-xs"
                    >
                      삭제
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* 새 변수 추가 */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-2">
              <input
                type="text"
                placeholder="변수명"
                value={newVariable.name}
                onChange={(e) => setNewVariable(prev => ({ ...prev, name: e.target.value }))}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-sm"
              />
              <select
                value={newVariable.type}
                onChange={(e) => setNewVariable(prev => ({ ...prev, type: e.target.value }))}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-sm"
              >
                {variableTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
              <input
                type="text"
                placeholder="설명 (선택)"
                value={newVariable.description}
                onChange={(e) => setNewVariable(prev => ({ ...prev, description: e.target.value }))}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-sm"
              />
              <button
                type="button"
                onClick={addVariable}
                className="px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
              >
                추가
              </button>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              HTML 내용에서 {'{{변수명}}'} 형태로 사용할 수 있습니다.
            </p>
          </div>

          {/* 액션 버튼 */}
          <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="submit"
              disabled={isLoading}
              className={`px-6 py-2 rounded-lg text-white font-medium ${
                isLoading 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-blue-600 hover:bg-blue-700 focus:ring-2 focus:ring-blue-500'
              }`}
            >
              {isLoading ? '저장 중...' : template ? '수정' : '생성'}
            </button>
            
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              취소
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}